import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { trpc, trpcClient } from "@/hooks/trpc";
import { incrementVersion } from "@brewbase/lib";
import type { FermentationProcedureId } from "@brewbase/db/types";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertCircleIcon,
  NotepadText,
  FileText,
  ListChecks,
  Droplets,
  ListOrdered,
  Calculator,
  LayoutDashboard,
  BellRing,
  LockIcon,
  CopyIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import type { SidebarNavItem } from "@/components/nav/sidebar-store";
import type { ProcedureWithDetails } from "@/features/fermentation-procedure-editor/types";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute("/fermentations/procedures/$procedure_id")(
  {
    component: ProcedureEditorLayout,
    params: z.object({
      procedure_id: z.coerce.number(),
    }),
    loader: async ({ params }) => {
      const procedure =
        await trpcClient.fermentationProcedures.getWithDetails.query(
          params.procedure_id as FermentationProcedureId,
        );
      return { procedureName: procedure?.name ?? "Procedure" };
    },
    head: ({ loaderData }) => ({
      meta: [{ title: `BrewBase - ${loaderData.procedureName}` }],
    }),
  },
);

function ProcedureEditorLayout() {
  const procedureId = Route.useParams().procedure_id as FermentationProcedureId;
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [isNewVersionDialogOpen, setIsNewVersionDialogOpen] = useState(false);
  const [newVersion, setNewVersion] = useState("");
  const [changelog, setChangelog] = useState("");

  const {
    data: procedure,
    isLoading,
    error,
  } = trpc.fermentationProcedures.getWithDetails.useQuery(procedureId);

  const createNewVersionMutation =
    trpc.fermentationProcedures.createVersion.useMutation({
      onSuccess: (data) => {
        toast.success(`New version "${data.version}" created!`);
        utils.fermentationProcedures.invalidate();
        setIsNewVersionDialogOpen(false);
        navigate({
          to: "/fermentations/procedures/$procedure_id/overview",
          params: { procedure_id: data.id },
        });
      },
      onError: (error) => {
        toast.error(`Failed to create new version: ${error.message}`);
      },
    });

  const deleteMutation = trpc.fermentationProcedures.delete.useMutation({
    onSuccess: () => {
      toast.success("Procedure deleted");
      utils.fermentationProcedures.invalidate();
      navigate({ to: "/fermentations/procedures" });
    },
    onError: (error) => {
      toast.error(`Failed to delete procedure: ${error.message}`);
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate(procedureId);
  };

  const handleCreateNewVersion = () => {
    if (!newVersion.trim()) {
      toast.error("Version is required");
      return;
    }
    createNewVersionMutation.mutate({
      sourceProcedureId: procedureId,
      newVersion: newVersion.trim(),
      changelog: changelog.trim() || undefined,
    });
  };

  const openNewVersionDialog = () => {
    if (procedure) {
      setNewVersion(incrementVersion(procedure.version));
    }
    setChangelog("");
    setIsNewVersionDialogOpen(true);
  };

  if (isLoading) {
    return <ProcedureEditorSkeleton />;
  }

  if (error || !procedure) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <AlertCircleIcon className="w-10 h-10 mx-auto text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">
            {error?.message ?? "Failed to load procedure"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ProcedureContent
        procedure={procedure}
        onCreateNewVersion={openNewVersionDialog}
        isCreatingNewVersion={createNewVersionMutation.isPending}
        onDelete={handleDelete}
        isDeleting={deleteMutation.isPending}
      />

      <Dialog
        open={isNewVersionDialogOpen}
        onOpenChange={setIsNewVersionDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Create a new version of "{procedure.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-version">New Version</Label>
              <Input
                id="new-version"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                placeholder="e.g., 2.0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="changelog">What changed? (optional)</Label>
              <Textarea
                id="changelog"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
                placeholder="Describe the changes in this version..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsNewVersionDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewVersion}
              disabled={createNewVersionMutation.isPending}
            >
              {createNewVersionMutation.isPending
                ? "Creating..."
                : "Create Version"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProcedureContent({
  procedure,
  onCreateNewVersion,
  isCreatingNewVersion,
  onDelete,
  isDeleting,
}: {
  procedure: ProcedureWithDetails;
  onCreateNewVersion: () => void;
  isCreatingNewVersion: boolean;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const navigation: SidebarNavItem[] = [
    {
      type: "link",
      id: "overview",
      label: "Overview",
      icon: FileText,
      href: `/fermentations/procedures/${procedure.id}/overview`,
    },
    {
      type: "link",
      id: "tasks",
      label: "Tasks",
      icon: ListChecks,
      href: `/fermentations/procedures/${procedure.id}/tasks`,
      count: procedure.tasks.length || undefined,
    },
    {
      type: "link",
      id: "lines",
      label: "Lines",
      icon: Droplets,
      href: `/fermentations/procedures/${procedure.id}/lines`,
      count: procedure.lines.length || undefined,
    },
    {
      type: "link",
      id: "protocols",
      label: "Protocols",
      icon: ListOrdered,
      href: `/fermentations/procedures/${procedure.id}/protocols`,
      count: procedure.sequence.length || undefined,
    },
    {
      type: "link",
      id: "kpis",
      label: "KPIs",
      icon: Calculator,
      href: `/fermentations/procedures/${procedure.id}/kpis`,
    },
    {
      type: "link",
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      href: `/fermentations/procedures/${procedure.id}/dashboard`,
    },
    {
      type: "link",
      id: "alerts",
      label: "Alerts",
      icon: BellRing,
      href: `/fermentations/procedures/${procedure.id}/alerts`,
    },
  ];

  // Build sidebar header actions
  const headerActions = (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onCreateNewVersion}
            disabled={isCreatingNewVersion}
          >
            <CopyIcon className="w-4 h-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {procedure.is_locked ? "Create new version" : "Create version"}
        </TooltipContent>
      </Tooltip>
      {!procedure.is_locked && (
        <DeleteConfirmDialog
          title="Delete Procedure"
          description={`Are you sure you want to delete "${procedure.name}"? This action cannot be undone.`}
          onConfirm={onDelete}
          isDeleting={isDeleting}
          trigger={
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              disabled={isDeleting}
            >
              <Trash2Icon className="w-4 h-4" />
            </Button>
          }
        />
      )}
    </>
  );

  useSidebarConfig({
    layer: 2,
    parent: {
      label: "Procedures",
      icon: NotepadText,
      href: "/fermentations/procedures",
    },
    header: {
      title: procedure.name,
      subtitle: `v${procedure.version}${procedure.is_locked ? " (Locked)" : ""}`,
      actions: headerActions,
    },
    navigation,
  });

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">
      {/* Locked warning banner */}
      {procedure.is_locked && (
        <Alert
          variant="destructive"
          className="rounded-none border-x-0 border-t-0"
        >
          <LockIcon className="h-4 w-4" />
          <AlertTitle>Procedure Locked</AlertTitle>
          <AlertDescription>
            This procedure has been used and cannot be edited. Create a new
            version to make changes.
          </AlertDescription>
        </Alert>
      )}
      <main className="flex-1 min-h-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

function ProcedureEditorSkeleton() {
  return (
    <div className="flex-1 flex flex-col">
      <div className="border-b px-4 py-2.5">
        <div className="flex items-center gap-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-16" />
        </div>
      </div>
      <div className="flex-1 p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}
